import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { format } from 'date-fns';
import { useSnackbar } from "notistack";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../app/store";
import EditIcon from "@mui/icons-material/Edit";
import { GridDeleteIcon } from "@mui/x-data-grid";
import ConfirmDialogList from "../../../components/ConfirmDialogList";
import configUrls from "../../../config/configUrls";
import { DocumentoComentarioList } from "../../../types/documento/comentario/DocumentoComentarioList";
import { DocumentoComentario, ISdocumentoComentario } from "../../../types/documento/comentario/DocumentoComentario";

import { useDeleteDocumentoComentarioMutation, useGetDocumentoComentariosQuery } from "./documentoComentarioApiSlice";

// Hook personalizado para gerenciar a busca
const useSearch = () => {
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const cursorPosition = useRef<number>(0);

    const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        cursorPosition.current = inputRef.current?.selectionStart || 0;
        setSearch(e.target.value);
    };

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            setDebouncedSearch(search);
        }, 700);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [search]);

    return {
        search,
        debouncedSearch,
        handleChangeSearch,
        inputRef,
        cursorPosition
    };
};

// Componente para renderizar as ações da tabela
const TableActions = ({ rowData, onDelete,idDocumento }: { rowData: DocumentoComentarioList, onDelete: (data: DocumentoComentarioList) => void,idDocumento: string }) => (
    <Box>
        <Tooltip title="Editar">
        <Link to={`${configUrls.DOCUMENTO_COMENTARIO_CREATE_LINK}${idDocumento}?idComentario=${rowData.id}`}>
                <IconButton color="secondary" aria-label="edit">
                    <EditIcon />
                </IconButton>
            </Link>
        </Tooltip>

        <Tooltip title="Excluir">
            <IconButton
                color="secondary"
                aria-label="delete"
                onClick={() => onDelete(rowData)}
            >
                <GridDeleteIcon />
            </IconButton>
        </Tooltip>
    </Box>
);

export const DocumentoComentarioListForm = () => {

 const idDocumento = useParams().id || "";


        const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { processoSelecionado } = useSelector((state: RootState) => state.processoSelecionado);
    const [loading, setLoading] = useState(false);
    const [comentarios, setComentarios] = useState<DocumentoComentarioList[]>([]);
    const [comentarioToDelete, setComentarioToDelete] = useState<DocumentoComentario>(ISdocumentoComentario);
    const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);

    const { search, debouncedSearch, handleChangeSearch, inputRef, cursorPosition } = useSearch();
    const [deleteComentario, { error: errorComentario, isSuccess }] = useDeleteDocumentoComentarioMutation();
    const { data, isFetching, error } = useGetDocumentoComentariosQuery({id: idDocumento ?? "", search: debouncedSearch});

    useEffect(() => {
        if (!processoSelecionado) {
            alert("Nenhum processo selecionado");
            window.location.href = configUrls.PROCESSO_LIST_RT;
        }
    }, [processoSelecionado]);

    useEffect(() => {
        if (data) {
            setComentarios(data);
        }
        if (error) {
            enqueueSnackbar("Comentários não encontrados!", { variant: "error" });
        } else if (isFetching) {
            enqueueSnackbar("Buscando registros...", { variant: "info" });
        }

        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
        }
    }, [data, error, isFetching, enqueueSnackbar, inputRef, cursorPosition]);

    useEffect(() => {
        if (isSuccess) {
            enqueueSnackbar("Registro deletado com sucesso!", { variant: "success" });
        }
        if (errorComentario) {
            enqueueSnackbar("Registro não deletado!", { variant: "error" });
        }
    }, [isSuccess, errorComentario, enqueueSnackbar]);

    const handleExcluirClick = (rowData: DocumentoComentarioList) => {
        setComentarioToDelete(rowData);
        setOpenConfirmExcluir(true);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirmExcluir(false);
        await deleteComentario({ id: comentarioToDelete.id ?? "" });
    };

    if (isFetching || loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    const renderData = (rowData: DocumentoComentarioList) => (
        <Typography color="secondary" variant="body2">
            {rowData.dataHoraCriacao ? format(rowData.dataHoraCriacao, 'dd/MM/yyyy HH:mm:ss') : "Data não disponível"}
        </Typography>
    );

    const renderUsuario = (rowData: DocumentoComentarioList) => (
        <Typography color="secondary" variant="body2">
            {rowData.usuario?.nome || "Usuário não disponível"}
        </Typography>
    );

    const renderDescricao = (rowData: DocumentoComentarioList) => (
        <Typography color="secondary" variant="body2">
            {rowData.descricao || ""}
        </Typography>
    );

    return (
        <>
            <ConfirmDialogList
                open={openConfirmExcluir}
                handleClose={() => setOpenConfirmExcluir(false)}
                title="Confirmar Ação"
                text={[
                    "Você tem certeza de que deseja excluir esse Comentário?",
                    comentarioToDelete.descricao ?? ""
                ]}
                handleDelete={handleConfirmDelete}
            />

            <Paper sx={{ width: "100%", height: "100vh", padding: "12px" }}>
                <Grid container padding={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography color="secondary" variant="h6" gutterBottom>
                                    Comentários para o documento: {processoSelecionado?.numeroProcesso}
                                </Typography>

                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"                                        
                                        onClick={() => { navigate(`${configUrls.DOCUMENTO_COMENTARIO_CREATE_LINK}${idDocumento}`) }}
                                    >
                                        Novo
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => navigate(-1)}
                                    >
                                        Voltar
                                    </Button>
                                </Box>
                            </Box>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} mt={5}>
                        <FormControl fullWidth>
                            <TextField
                                label="Digite para pesquisar"
                                name="nome"
                                onChange={handleChangeSearch}
                                size="small"
                                inputProps={{ maxLength: 100 }}
                                value={search}
                                inputRef={inputRef}
                            />
                        </FormControl>
                    </Grid>
                </Grid>

                <DataTable
                    value={comentarios}
                    paginator
                    rows={25}
                    showGridlines={true}
                    size="small"
                    columnResizeMode="expand"
                    resizableColumns
                    dataKey="id"
                    header={
                        <Typography variant="body2" color="textSecondary">
                            Comentário(s) encontrado(s): {comentarios.length}
                        </Typography>
                    }
                    className="custom-datatable"
                >
                    <Column header="Data" body={renderData} sortable style={{ width: '20%' }} />
                    <Column header="Descrição" body={renderDescricao} sortable style={{ width: '70%' }} />
                    <Column header="Usuário" body={renderUsuario} sortable style={{ width: '20%' }} />
                    <Column
                        field="acoes"
                        header="Ações"
                        body={(rowData) => <TableActions rowData={rowData} onDelete={handleExcluirClick} idDocumento={idDocumento}/>}
                        style={{ width: 'auto' }}
                    />
                </DataTable>
            </Paper>
        </>
    );
};
